import { forwardRef, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";

import type { TextInputProps } from "@ui/components/Inputs/TextInput";
import { TextInput } from "@ui/components/Inputs/TextInput";
import { RoundButton } from "@ui/components/RoundButton";
import { useDynamicScript } from "@ui/hooks/useDynamicScript";
import { useOutsideClick } from "@ui/hooks/useOutsideClick";
import { useTranslations } from "@ui/providers/useTranslations";
import classNames from "classnames";

import { useConfig } from "@jti/app-context/useConfig";
import { UiCrossIcon } from "@jti/brand-icons/ui-cross";
import { Loader } from "@jti/core-components/components/Loader";

import type { PlaceInputResults } from "../../PlaceInput";
import type { DaumPostcodeData, DaumWidgetInstance } from "../../types/daumWidget";

import styles from "./DaumPlaceInput.module.scss";

export type DaumPlaceInputProps = {
  onPlace?: (result: PlaceInputResults) => void;
} & TextInputProps;

const prepareDaumAutocompleteResults = (data: DaumPostcodeData): PlaceInputResults => {
  const city = data.sigungu || data.bname1 || data.sido || "";
  const streetName = data.address || data.roadname || data.roadAddress;

  return {
    streetName,
    streetNumber: "",
    postcode: data.zonecode,
    city,
    provinceName: data.sido || "",
    districtName: data.bname || "",
  };
};

const MAX_MOBILE_WIDTH = 479;

export const DaumPlaceInput = forwardRef<HTMLInputElement, DaumPlaceInputProps>(({ onPlace, ...props }, ref) => {
  const [widget, setWidget] = useState<DaumWidgetInstance>();
  const [isWidgetOpened, setWidgetOpened] = useState(false);
  const { autocompleteProviderExternalJs } = useConfig();
  const { isScriptLoaded, isScriptLoading } = useDynamicScript(autocompleteProviderExternalJs);
  const widgetContainerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery({ maxWidth: MAX_MOBILE_WIDTH });
  const { __ } = useTranslations();

  const closeWidget = () => {
    setWidgetOpened(false);
  };
  useOutsideClick(widgetContainerRef, () => {
    closeWidget();
  });

  const openWidget = () => {
    if (!widget || !widgetContainerRef.current) return;
    setWidgetOpened(true);
    widget.embed(widgetContainerRef.current);
  };

  useEffect(() => {
    const postcodeWidget = window.kakao ?? window.daum;
    if (!isScriptLoaded || !postcodeWidget) {
      return;
    }

    setWidget(
      new postcodeWidget.Postcode({
        width: isMobile ? "100%" : "",
        height: isMobile ? "100%" : "",
        oncomplete: (data: DaumPostcodeData) => {
          onPlace?.(prepareDaumAutocompleteResults(data));
          closeWidget();
        },
      })
    );
  }, [onPlace, isScriptLoaded]);

  if (isScriptLoading) {
    return <Loader />;
  }

  return (
    <div className={styles.daumAutocompleteContainer}>
      <TextInput
        {...props}
        readOnly
        ref={ref}
        onClick={openWidget}
        containerClassName={styles.daumAutocompleteWidgetInput}
      />
      <div
        className={classNames(styles.daumAutocompleteWidgetContainer, {
          [styles.daumAutocompleteWidgetInactive]: !isWidgetOpened,
        })}
        ref={widgetContainerRef}
      >
        <div className={styles.mobileWidgetCloseIcon}>
          <RoundButton icon={UiCrossIcon} onClick={closeWidget} aria-label={__("common.close")} />
        </div>
      </div>
    </div>
  );
});
